import { Button } from "antd";
import Image from "next/image";
import { Component, ErrorInfo, ReactNode } from "react";

import { captureError } from "@/sentry";

import emptyStateIllustration2 from "@/assets/empty-illustration-2.png";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // You can also log the error to an error reporting service
    this.setState({ hasError: true, errorInfo });

    captureError(error, {
      extra: {
        errorInfo: errorInfo.componentStack
      }
    })
  }

  handleReload = () => {
    window.location.reload();
  };

  handleBack = () => {
    window.history.back();
  };

  render() {
    if (this.state.hasError) {
      const { errorInfo } = this.state;
      // You can render any custom fallback UI
      return (
        <main className="w-full h-screen flex items-center justify-center bg-neutral-900">
          <div className="bg-neutral-950 px-4 py-10 rounded relative text-center lg:w-2/5 w-full">
            <Image
              src={emptyStateIllustration2}
              height={80}
              width={80}
              alt="empty state"
              className="inline-block"
            />
            <div>
              <h1 className="font-semibold text-2xl mt-4">Oops...!</h1>
              <p className="block text-base text-neutral-300">Something went wrong.</p>
            </div>
            {errorInfo && (
              <div className="mt-4 bg-neutral-800 px-3 py-2 rounded-sm text-neutral-300 truncate font-mono">
                {errorInfo.componentStack}
              </div>
            )}
            <div className="mt-4 space-x-4">
              <Button type="primary" onClick={this.handleReload}>
                Reload Page
              </Button>
              <Button type="default" onClick={this.handleBack}>
                Go Back
              </Button>
            </div>
          </div>
        </main>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
